/**
 * 播放器和房间层需要互相传递消息，由于播放器一般由品类实现，统一事件管理机制
 */
import EventBus from '../../utils/event';
import { RoomAction, PlayerAction, RemotePlayMessage } from '../../message_const';
import type { IMediaSyncInfo } from '@src/slide/application/service/remote-media-sync-manager/core/interface';

export interface ISlidePlayerState {
    currentPage: number;
    currentFragment: number;
    pageCount: number;
    laserTurnOn: boolean;
    enableAnnotation: boolean;
    isManulFlipped: boolean;
    scale: number;
    iaFragmentInfo: any; // 这个字段不太明白意义，先 any
    shouldStopMainAnim: boolean;
    isLoadPageBySwitchForward: boolean,
    shouldStopTransition: boolean;
}

interface PlayerEvent {
    [PlayerAction.PlayerState]: (value: ISlidePlayerState) => void;
    [PlayerAction.GotoPageFragment]: (value: ISlidePlayerState) => void; // 重构 tip: 这个事件感觉用不上这么多数据
    [PlayerAction.PlayerReady]: (value: { playerInitData: Record<string, unknown> }) => void;
    [PlayerAction.MobileOpenToolBar]: () => void;
    [PlayerAction.EndPlay]: (value: { pageIndex: number }) => void;
    [PlayerAction.Loading]: () => void;
    [PlayerAction.DataLoading]: (value: { loadingText: string }) => void;
    [PlayerAction.CancelDataLoading]: () => void;
    [PlayerAction.Error]: (value: { lastError: string }) => void;
    [PlayerAction.LinkClicked]: (value: { href: string }) => void;
    [PlayerAction.PlayerMouseMove]: (value: { X: number; Y: number }) => void;
    [PlayerAction.Media]: (value: IMediaSyncInfo) => void;
    [PlayerAction.TurnLaser]: (enable: boolean) => void;
    [PlayerAction.AnnotationMessage]: (value: any) => void;
    [PlayerAction.TurnAnnotation]: (value: { enable: boolean, initData?: {} }) => void;
}

interface LaserInfo {
    laserInfo: {
        X: number;
        Y: number;
        ts: number;
    };
}

interface RemoteChangePageInfo {
    currentPage: number;
    currentFragment: number;
    iaFragmentInfo: any;
    shouldStopMainAnim: boolean;
    isLoadPageBySwitchForward: boolean;
    shouldStopTransition?: boolean;
}

interface RoomEvent {
    [RoomAction.RemoteChangePage]: (value: RemoteChangePageInfo) => void;
    [RoomAction.RemoteAllowFlipOn]: () => void;
    [RoomAction.RemoteAllowFlipOff]: () => void;
    [RoomAction.BeAudience]: () => void;
    [RoomAction.LaserTrunOn]: () => void;
    [RoomAction.LaserTrunOff]: () => void;
    [RoomAction.PrevPage]: () => void;
    [RoomAction.NextPage]: () => void;
    [RoomAction.ExitShow]: () => void;
    [RoomAction.TurnLaser]: () => void;
    [RoomAction.SyncWithMaster]: () => void;
    [RoomAction.PopWindowShow]: () => void;
    [RoomAction.PopWindowHide]: () => void;
    [RoomAction.ShowPlayerContent]: (value: { currentPage: number; currentRev: number }) => void;
    [RoomAction.LiveBegin]: (value: { currentRev: number; currentPage: number; currentFragment: number }) => void;
    [RoomAction.ZoomIn]: () => void;
    [RoomAction.ZoomOut]: () => void;
    [RoomAction.ToggleView]: () => void;
    [RoomAction.RemoteLaserMove]: (value: LaserInfo) => void;
    [RoomAction.RemoteStopMainAnim]: () => void;
    [RoomAction.MediaStateChanged]: (value: IMediaSyncInfo) => void;
    [RoomAction.TurnAnnotation]: (value: { enable: boolean, initData?: {} }) => void;
    [RoomAction.AnnotationMessage]: (value: any) => void;
}

class MeetingEventBus {
    private static instance: MeetingEventBus;
    public static getInstance() {
        if (!MeetingEventBus.instance) {
            MeetingEventBus.instance = new MeetingEventBus();
        }
        return MeetingEventBus.instance;
    }

    /**
     * 历史遗留，控制播放器是否可以发送消息
     */
    public playerSendEnable = true;

    private eventBus: EventBus;
    private subFrame: HTMLElement;

    private constructor() {
        this.eventBus = new EventBus();
        window.addEventListener('message', this.handleMessageEvent, false);
    }

    /**
     * 播放器发送事件到房间
     */
    public sendPlayerEvent2Room<T extends PlayerAction>(event: T, ...value: Parameters<PlayerEvent[T]>) {
        if (!this.playerSendEnable) {
            return;
        }
        // iframe 模式传输信息（单独播放器）
        if (/meetingroom\/slide\/player/.test(location.pathname)) {
            const args = value[0] ?? [];
            this.iframeSendPlayerAction({ playerAction: event, ...args });
        }
        this.eventBus.emit(`player-event-${event}`, ...value);
    }

    /**
     * 房间发送事件到播放器
     */
    public sendRoomEvent2Player<T extends RoomAction>(event: T, ...value: Parameters<RoomEvent[T]>) {
        // iframe 模式传输信息(非slide)
        if (!/slide/.test(location.pathname)) {
            const args = value[0] ?? [];
            this.iframeSendRoomAction({ roomAction: event, ...args }, this.subFrame);
        }
        this.eventBus.emit(`room-event-${event}`, ...value);
    }

    /**
     * 接收播放器事件
     */
    public onRecvPlayerEvent<T extends PlayerAction>(event: T, cb: PlayerEvent[T]) {
        this.eventBus.on(`player-event-${event}`, cb);
        return () => this.eventBus.off(`player-event-${event}`, cb);
    }

    /**
     * 接收房间事件
     */
    public onRecvRoomEvent<T extends RoomAction>(event: T, cb: RoomEvent[T]) {
        this.eventBus.on(`room-event-${event}`, cb);
        return () => this.eventBus.off(`room-event-${event}`, cb);
    }

    public set playerFrame(val: HTMLIFrameElement) {
        this.subFrame = val;
    }

    /* ------- 下面都是处理 iframe 场景的函数, copy 自原来的代码 ---------*/

    // 处理Message事件回调
    private handleMessageEvent = (event) => {
        let eventData = event.data;
        if (typeof eventData === 'string' && eventData.substr(0, 5) === 'tdocs') {
            eventData = eventData.slice(5);
            const msgObj = JSON.parse(eventData);

            switch (msgObj.msgType) {
                case RemotePlayMessage.PlayerStateChange:
                    this.handlePlayerAction(msgObj.msgData);
                    break;
                case RemotePlayMessage.RoomStateChange:
                    this.handleRoomAction(msgObj.msgData);
                    break;
                default:
                    break;
            }
        }
    };

    private handlePlayerAction = (msgData) => {
        const msgObj = JSON.parse(msgData);
        const { playerAction }: { playerAction: PlayerAction } = msgObj;
        this.eventBus.emit(`player-event-${playerAction}`, msgObj);
    };

    private handleRoomAction = (msgData) => {
        const msgObj = JSON.parse(msgData);
        const { roomAction }: { roomAction: RoomAction } = msgObj;
        this.eventBus.emit(`room-event-${roomAction}`, msgObj);
    };

    // 发送 room 消息
    private iframeSendRoomAction = (roomAction, subFrame) => {
        const dataStr = JSON.stringify(roomAction);
        const dataObj = { msgType: RemotePlayMessage.RoomStateChange, msgData: dataStr };
        const postDataStr = `tdocs${JSON.stringify(dataObj)}`;
        const contentWindow = subFrame?.contentWindow;
        contentWindow?.postMessage?.(postDataStr, '*');
    };

    // 发送 player 消息
    private iframeSendPlayerAction = (playerAction) => {
        const dataStr = JSON.stringify(playerAction);
        const dataObj = { msgType: RemotePlayMessage.PlayerStateChange, msgData: dataStr };
        const postDataStr = `tdocs${JSON.stringify(dataObj)}`;
        window.parent?.postMessage?.(postDataStr, '*');
    };
}

export default MeetingEventBus.getInstance();
